import { useCallback, useState } from 'react';
import { SearchIcon, UsersIcon } from 'lucide-react';
import { Panel } from '../ui/Panel';
import { Badge } from '../ui/Badge';
import { EmptyState } from '../ui/EmptyState';
import { LoadFailed, LoadingLine } from '../ui/LoadState';
import { Tabs } from '../ui/Tabs';
import { Segments } from './Segments';
import { fetchSubscribers } from '../adminApi';
import { useLatestLoad } from '../../hooks/useLatestLoad';
import { useCan } from '../useCan';
import { btnSecondary, inputCls } from '../ui/styles';
import { fromIso, shortDate } from '../../core/format/dates';
import type { RequestOptions } from '../../core/types/backend';
import type { SubscriberPage, SubscriberStatus } from '../../core/types/mail';
import type { SegmentsState } from './useSegments';
const statusTabs = [{
  key: 'alle',
  label: 'Alle'
}, {
  key: 'bevestigd',
  label: 'Bevestigd'
}, {
  key: 'onbevestigd',
  label: 'Wacht op bevestiging'
}, {
  key: 'afgemeld',
  label: 'Afgemeld'
}, {
  key: 'onbestelbaar',
  label: 'Onbestelbaar'
}] as const;
type StatusTab = (typeof statusTabs)[number]['key'];
const statusBadge: Record<SubscriberStatus, {
  label: string;
  tone: 'success' | 'soft' | 'neutral' | 'accent';
}> = {
  bevestigd: {
    label: 'Bevestigd',
    tone: 'success'
  },
  onbevestigd: {
    label: 'Wacht',
    tone: 'soft'
  },
  afgemeld: {
    label: 'Afgemeld',
    tone: 'neutral'
  },
  onbestelbaar: {
    label: 'Onbestelbaar',
    tone: 'accent'
  }
};
const sourceLabel = {
  footer: 'Footer',
  account: 'Mijn omgeving',
  quiz: 'Huidadvies',
  import: 'Import'
} as const;

/**
 * Tabblad Abonnees & doelgroepen. Wie de lijst met e-mailadressen niet mag zien (Marketing: geen
 * klantgegevens) maakt hier alleen doelgroepen; hoeveel lezers erin vallen telt de server.
 */
export function Subscribers({
  segments
}: {
  segments: SegmentsState;
}) {
  const {
    canAll
  } = useCan();
  if (!canAll('customers.view')) {
    return <div className="grid gap-6 lg:grid-cols-12" data-id="element-2722" data-mp="24a24ety9mf4r">
        <div className="min-w-0 lg:col-span-7" data-id="element-2723" data-mp="24bvb298vozcr">
          <Segments seg={segments} data-id="element-2724" data-mp="arbixv7rjnzs" />
        </div>
        <p className="text-[13px] text-muted lg:col-span-5" data-id="element-2725" data-mp="ar1u4aygfmvo">
          De lijst met abonnees en hun e-mailadressen ziet alleen wie klantgegevens mag inzien. Je doelgroepen werken gewoon: de server telt wie erin valt en mailt hen.
        </p>
      </div>;
  }
  return <SubscriberList segments={segments} data-id="element-2726" data-mp="3s8ustlfje8h9" />;
}
function SubscriberList({
  segments
}: {
  segments: SegmentsState;
}) {
  const [q, setQ] = useState('');
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState<StatusTab>('alle');
  const [page, setPage] = useState(1);
  const load = useCallback((opts: RequestOptions) => fetchSubscribers({
    q: query || undefined,
    status: status === 'alle' ? undefined : status,
    page
  }, opts), [query, status, page]);
  const {
    data,
    status: loadStatus,
    reload
  } = useLatestLoad(load);
  return <div className="space-y-6" data-id="element-2727" data-mp="1cekq9vx70aiv">
      <Stats data={data} data-id="element-2728" data-mp="17hc7pu84u4h3" />

      <div className="grid gap-6 lg:grid-cols-12" data-id="element-2729" data-mp="17j5ct533kgmz">
        <div className="min-w-0 lg:col-span-7" data-id="element-2730" data-mp="1f1czfwl864eh">
          <Panel flush title="Abonnees" actions={data && <span className="text-[13px] tabular-nums text-muted" data-id="element-2732" data-mp="1k0g4vi54acmz">{data.total}</span>} data-id="element-2731" data-mp="1f0lfxk4wjx4k">
            <div className="space-y-3 border-b border-line px-4 py-4 sm:px-5" data-id="element-2733" data-mp="1jy3dyfhdsrxs">
              <form role="search" onSubmit={e => {
              e.preventDefault();
              setPage(1);
              setQuery(q.trim());
            }} className="relative" data-id="element-2734" data-mp="1ory6m8eysh90">
                <label htmlFor="abonnee-zoek" className="sr-only" data-id="element-2735" data-mp="110si74uykgn3">
                  Zoek op e-mail of naam
                </label>
                <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" strokeWidth={1.75} aria-hidden="true" data-id="element-2736" data-mp="15z3t7iwzox6w" />
                <input id="abonnee-zoek" type="search" value={q} onChange={e => setQ(e.target.value)} placeholder="Zoek op e-mail of naam" className={`${inputCls} pl-9`} data-id="element-2737" data-mp="2hgdbw38anl75" />
              </form>
              <Tabs label="Status" variant="pill" tabs={statusTabs} value={status} onChange={s => {
              setStatus(s);
              setPage(1);
            }} data-id="element-2738" data-mp="2jzqexqkmrgdh" />
            </div>
            {loadStatus === 'error' ? <LoadFailed title="De abonnees konden niet laden" onRetry={reload} className="m-4" data-id="element-2739" data-mp="2oxrw25yhx5mb" /> : !data ? <LoadingLine label="Abonnees laden…" className="px-5 py-8 text-center" data-id="element-2740" data-mp="32x1ap171zro6" /> : data.items.length === 0 ? <div className="p-4" data-id="element-2741" data-mp="3consxhht6r0f">
                <EmptyState icon={UsersIcon} title={query ? 'Niemand gevonden' : 'Nog geen abonnees in deze lijst'} text={query ? `Niets voor “${query}”.` : 'Aanmeldingen uit de footer en Mijn omgeving komen hier.'} data-id="element-2742" data-mp="3cnma2qujx085" />
              </div> : <ul className={`divide-y divide-line ${loadStatus === 'loading' ? 'opacity-60' : ''}`} aria-busy={loadStatus === 'loading'} data-id="element-2743" data-mp="3hoa7pvdacfbc">
                {data.items.map(s => <li key={s.id} className="flex items-center gap-3 px-4 py-3 sm:px-5" data-id="element-2744" data-mp="52ldtadwjmii">
                    <span className="min-w-0 flex-1" data-id="element-2745" data-mp="93x29cl1i1p4">
                      <span className="block truncate text-[14px] text-ink" data-id="element-2746" data-mp="3rakxmaqafahl">{s.email}</span>
                      <span className="block truncate text-[12px] text-muted" data-id="element-2747" data-mp="4jssjwx1d4e">
                        {s.firstName ? `${s.firstName} · ` : ''}
                        {sourceLabel[s.source]} · aangemeld {shortDate(fromIso(s.subscribedAt))}
                        {s.customerId ? ' · klant' : ''}
                      </span>
                    </span>
                    <Badge tone={statusBadge[s.status].tone} data-id="element-2748" data-mp="38jbm5vwyibnd">{statusBadge[s.status].label}</Badge>
                  </li>)}
              </ul>}
            {data && data.pageCount > 1 && <nav aria-label="Pagina's" className="flex items-center justify-between gap-3 border-t border-line px-4 py-3 sm:px-5" data-id="element-2749" data-mp="33m1xwjlb0614">
                <button type="button" onClick={() => setPage(p => p - 1)} disabled={data.page <= 1} className={btnSecondary} data-id="element-2750" data-mp="2kii1asw4qllz">
                  Vorige
                </button>
                <span className="text-[13px] tabular-nums text-muted" data-id="element-2751" data-mp="2ar3vk3dl4pke">
                  {data.page} van {data.pageCount}
                </span>
                <button type="button" onClick={() => setPage(p => p + 1)} disabled={data.page >= data.pageCount} className={btnSecondary} data-id="element-2752" data-mp="25u3qnvsh1suv">
                  Volgende
                </button>
              </nav>}
          </Panel>
        </div>
        <div className="min-w-0 lg:col-span-5" data-id="element-2753" data-mp="2thlkrxz7l310">
          <Segments seg={segments} data-id="element-2754" data-mp="2tga796fyogwp" />
        </div>
      </div>
    </div>;
}

/** Eén groot getal (wie de nieuwsbrief krijgt), daarnaast groei en wat er afvalt. */
function Stats({
  data
}: {
  data: SubscriberPage | null;
}) {
  if (!data) return null;
  const {
    stats
  } = data;
  const max = Math.max(1, ...stats.weeks.map(w => w.joined));
  return <section aria-label="Groei" className="grid gap-6 rounded-lg border border-line bg-white p-5 sm:p-6 lg:grid-cols-12" data-id="element-2755" data-mp="3orqjmhop7ku5">
      <div className="lg:col-span-4" data-id="element-2756" data-mp="3trcuikh20ozs">
        <p className="font-display text-5xl font-light tabular-nums text-ink" data-id="element-2757" data-mp="3to8ln9sn952s">{stats.confirmed}</p>
        <p className="mt-1 text-[13px] text-muted" data-id="element-2758" data-mp="2hxfiz8s1e4l">bevestigde lezers, {stats.joinedLastWeek} nieuw in de afgelopen week</p>
        <dl className="mt-4 grid grid-cols-3 gap-3 text-[13px]" data-id="element-2759" data-mp="c9tm6yoxickf">
          <div data-id="element-2760" data-mp="c9jzpl33enjh">
            <dt className="text-muted" data-id="element-2761" data-mp="cc62l7f1gevd">Wacht</dt>
            <dd className="tabular-nums text-ink" data-id="element-2762" data-mp="gadmuc8pnybb">{stats.pending}</dd>
          </div>
          <div data-id="element-2763" data-mp="la8c6bcu8fu9">
            <dt className="text-muted" data-id="element-2764" data-mp="lbjcg172r5gm">Afgemeld</dt>
            <dd className="tabular-nums text-ink" data-id="element-2765" data-mp="q9kxgro29euq">{stats.unsubscribed}</dd>
          </div>
          <div data-id="element-2766" data-mp="q78jeplfbomy">
            <dt className="text-muted" data-id="element-2767" data-mp="2n24m5gjcxhmx">Onbestelbaar</dt>
            <dd className="tabular-nums text-ink" data-id="element-2768" data-mp="2n3fqpikmmtf3">{stats.bounced}</dd>
          </div>
        </dl>
      </div>
      <figure className="min-w-0 lg:col-span-8" data-id="element-2769" data-mp="2s17rz375vrvy">
        <figcaption className="mb-3 text-[13px] text-muted" data-id="element-2770" data-mp="xslr8l927u4w">Aanmeldingen per week, laatste 12 weken</figcaption>
        <ol className="flex h-28 items-end gap-1.5" data-id="element-2771" data-mp="sszgqjkm8rzs">
          {stats.weeks.map(w => <li key={w.week} className="flex h-full flex-1 flex-col justify-end" title={`Week van ${shortDate(fromIso(w.week))}: ${w.joined} erbij, ${w.left} eraf`} data-id="element-2772" data-mp="17j6p26gwsumr">
              <span className="sr-only" data-id="element-2773" data-mp="eu9j92tvwqaw">
                Week van {shortDate(fromIso(w.week))}: {w.joined} erbij, {w.left} eraf
              </span>
              <span className="block rounded-t-sm bg-clay/80" style={{
            height: `${w.joined / max * 100}%`,
            minHeight: w.joined ? 4 : 0
          }} aria-hidden="true" data-id="element-2774" data-mp="9wgeme88c4cq" />
              <span className="block h-px bg-line" aria-hidden="true" data-id="element-2775" data-mp="jtm5pmgufvcy" />
            </li>)}
        </ol>
      </figure>
    </section>;
}